import {useState, useMemo, useCallback, useEffect} from 'react';

import moment from 'moment-timezone';
import {some, find, sortBy, sumBy, groupBy} from 'lodash';

import {PaymentMethodTypes} from '~/shared/consts/paymentConsts';
import {EMPTY_ARRAY} from '~/shared/consts/commonConsts';
import {getLocalizationService} from '~/shared/services/localisationService';
import {mapTranslate} from '~/shared/consts/userTransactionsConsts';

import MobileTransactionTable from '../MobileTransactionTable';
import DesktopTransactionTable from '../DesktopTransactionTable';

import {getColumns} from './utils/getColumns';

const orderGuestsEnum = {
  None: 'None',
  International: 'international_guest',
  Contractor: 'contractor_guest',
  Temp: 'temp_guest',
  BusinessMeeting: 'business_guest',
  Personal: 'personal_guest',
};

const TransactionTable = props => {
  const {
    setFilter,
    orderList,
    currentLanguageKey,
    dateBias,
    showTransferDebit,
    debitTransfersSentRequests,
    showEmployerEmployeePart,
    shouldShowPaymentRemarks,
    prePaidCard,
    isAutoRefundRemarksPatchIncluded,
    showGrossNet,
    isKlaReport,
    shouldShowCommentsRows,
    filter: selectedFilter,
    totals,
    totalGross,
    totalNett,
    checkIfMoneycardIsCredit,
    renderDesktopComponent,
  } = props;

  const {t} = getLocalizationService();
  const columns = getColumns(isKlaReport, prePaidCard, t);

  const [openRows, setOpenRows] = useState(EMPTY_ARRAY);
  const [visibleItems, setVisibleItems] = useState([]);
  const [extraHeightForOrderComments, setExtraHeightForOrderComments] = useState(0);

  const toggleRow = id => {
    if (!id) {
      return;
    }
    const newOpenRows = openRows.includes(id) ? openRows.filter(orderId => orderId !== id) : [...openRows, id];
    setOpenRows(newOpenRows);
  };

  const clearOpenedRows = () => setOpenRows(EMPTY_ARRAY);

  const getOrderListWithPaymentsAndDishes = useCallback(() => {
    /* transforming data to fit the react-virtualized's table component, every payment and product would
     * have its own row in the data with parentId(the orderId of the payment/product). */
    if (!orderList) {
      return;
    }

    const orderListSortedByOrderDate = sortBy(orderList, ['orderDate']).reverse();
    const ordersGroupedByDateStr = groupBy(orderListSortedByOrderDate, 'orderDateStr');

    return orderListSortedByOrderDate.reduce((allOrders, order) => {
      const {
        orderId: parentId,
        orderDateStr,
        orderTimeStr,
        restaurantName,
        restaurantId,
        orderType,
        total,
        products,
        payments,
        existTransferRequest: existTransferRequestFromServer,
        reviewTransactionId,
        review,
        restaurantReviewsDisabled,
        inoviceUrls,
        deliveryComments,
        transferDebit,
        isBarCodeOrder,
        isCanceled,
        employeePart,
        employerPart,
        orderDate,
        transactionBrutoSum,
        transactionNetSum,
      } = order;
      const currentTransactionsInGroup = ordersGroupedByDateStr[orderDateStr];
      const totalSummary = sumBy(currentTransactionsInGroup, 'total');
      const isOrderPaidWithMoneyCard = some(payments, {paymentMethod: PaymentMethodTypes.MONEY_CARD});
      const isFirstDateBias = dateBias === 0;
      const isTerminalOrderType = orderType === 'terminal';
      const existTransferRequest = some(debitTransfersSentRequests, {orderId: parentId});
      const orderPayment = payments.find(({paymentId}) => paymentId === prePaidCard?.moneycardId);

      const orderDaysAgo = moment().diff(orderDate, 'days');
      const allowedToSetReview = orderDaysAgo <= 14;

      allOrders.push({
        id: parentId,
        dateAndTime: `${orderDateStr} ${orderTimeStr}`,
        restaurantName,
        restaurantId,
        isBarCodeOrder,
        isCanceled,
        reviewTransactionId,
        orderTypeOrigin: orderType,
        orderType: t(mapTranslate[orderType] || orderType),
        payments,
        products,
        employeePart,
        employerPart,
        total,
        orderTotal: total,
        inoviceUrls,
        shouldShowPaymentRemarks,
        isAutoRefundRemarksPatchIncluded,
        showEmployerEmployeePart,
        showGrossNet,
        transactionBrutoSum,
        transactionNetSum,
        guestType: order.guestType !== orderGuestsEnum.None && t(orderGuestsEnum[order.guestType]),
        shouldShowOrderMenuButton: !isTerminalOrderType,
        shouldShowDebitTransferMenuButton:
          showTransferDebit &&
          isOrderPaidWithMoneyCard &&
          isTerminalOrderType &&
          !existTransferRequest &&
          !existTransferRequestFromServer &&
          isFirstDateBias &&
          total > 0,
        shouldShowDownloadInvoiceMenuButton: inoviceUrls?.length,
        shouldShowAddAReviewButton: review?.reviewId === 0 && !restaurantReviewsDisabled && allowedToSetReview,
        shouldShowDisplayReviewButton: review?.reviewId !== 0 && !restaurantReviewsDisabled && !allowedToSetReview,
        shouldShowEditAReviewButton: review?.reviewId !== 0 && !restaurantReviewsDisabled && allowedToSetReview,
        review,
        prepaidBalance: orderPayment?.prepaidBalance && Number(orderPayment.prepaidBalance),
        isKlaReport,
        totalSummary,
      });

      // sub-rows
      let productsRows = EMPTY_ARRAY;
      let commentsRows;
      let debitTransferRows;
      let paymentsRows = EMPTY_ARRAY;
      let employerEmployeeCut = EMPTY_ARRAY;
      let grossNettSplit = EMPTY_ARRAY;
      let guestRow;
      let dailySumRow;

      if (products) {
        productsRows = products.map(({name, price}, index) => ({
          parentId,
          dateAndTime: index === 0 ? t('products') : '',
          restaurantName: name,
          total: price,
          isFirstRow: index === 0,
          isLastRow: products.length === index + 1,
        }));
      }

      if (deliveryComments && shouldShowCommentsRows) {
        const NUMBER_OF_CHARS_TO_BREAK = 71;
        commentsRows = {
          parentId,
          dateAndTime: t('order_notes'),
          restaurantName: deliveryComments,
          numberOfLines: Math.ceil(deliveryComments.length / NUMBER_OF_CHARS_TO_BREAK),
          isCommentRow: true,
          isFirstRow: true,
          isLastRow: true,
        };
      }

      if (payments) {
        let isPaymentRemarksRenderedOnce = false;
        const getCurrentResuarantName = (cardSuffix, paymentMethod, paymentId) => {
          if (PaymentMethodTypes.APPLE_PAY === paymentMethod) {
            return t('applepay');
          }

          if (PaymentMethodTypes.GOOGLE_PAY === paymentMethod) {
            return 'Google Pay';
          }

          if (checkIfMoneycardIsCredit(paymentId)) {
            return t('tenbis_credit');
          }
          if (currentLanguageKey === 'he') {
            return `${cardSuffix || ''} ${t(paymentMethod.toLowerCase())}`;
          }

          return `${t(paymentMethod.toLowerCase())} ${cardSuffix || ''}`;
        };

        paymentsRows = payments.reduce(
          (
            allPaymentsRows,
            {cardSuffix, paymentMethod, total: totalForCurrentPayment, paymentRemarks, autoRefundRemarks, paymentId},
            index,
          ) => {
            const shouldShowautoRefundRemarks = isAutoRefundRemarksPatchIncluded && autoRefundRemarks;
            if (shouldShowautoRefundRemarks) {
              // autoRefundRemarks has individual item in the payments array.
              // so the totalForCurrentPayment represent the amount of discount.
              return allPaymentsRows.concat({
                parentId,
                restaurantName: autoRefundRemarks,
                total: totalForCurrentPayment,
                isLastRow: shouldShowPaymentRemarks ? false : payments.length === index + 1 || payments.length === 1,
              });
            }

            let newAllPaymentsRows = allPaymentsRows.concat({
              parentId,
              dateAndTime: index === 0 ? t('payment') : '',
              restaurantName: getCurrentResuarantName(cardSuffix, paymentMethod, paymentId),
              total: totalForCurrentPayment,
              isFirstRow: index === 0,
              isLastRow: shouldShowPaymentRemarks ? false : payments.length === index + 1 || payments.length === 1,
            });

            if (shouldShowPaymentRemarks) {
              newAllPaymentsRows = [
                ...newAllPaymentsRows,
                {
                  parentId,
                  dateAndTime: isPaymentRemarksRenderedOnce ? '' : t('payment_remark_case'),
                  restaurantName: paymentRemarks,
                  isCommentRow: true,
                  isLastRow: !shouldShowautoRefundRemarks && (payments.length === index + 1 || payments.length === 1),
                },
              ];
              isPaymentRemarksRenderedOnce = true;
            }
            return newAllPaymentsRows;
          },
          [],
        );
      }

      if (transferDebit) {
        debitTransferRows = {
          parentId,
          dateAndTime: t('debit_transfer'),
          restaurantName: t('debit_transfer_from_xxxx_xxxx', {
            cardOwner: transferDebit.cardOwner,
            cardSuffix: transferDebit.cardSuffix,
          }),
          total,
          isFirstRow: true,
          isLastRow: true,
        };
      }

      if (showEmployerEmployeePart) {
        employerEmployeeCut = [
          {
            parentId,
            dateAndTime: t('division'),
            restaurantName: t('employer_part'),
            total: employerPart,
            isFirstRow: true,
            isLastRow: false,
          },
          {
            parentId,
            restaurantName: t('employee_part'),
            total: employeePart,
            isFirstRow: false,
            isLastRow: true,
          },
        ];
      }

      const isLastTransactionOfTheDay =
        currentTransactionsInGroup.findIndex(({orderId}) => orderId === parentId) ===
        currentTransactionsInGroup.length - 1;
      if (isKlaReport && isLastTransactionOfTheDay) {
        dailySumRow = {
          parentId,
          dateAndTime: t('daily_summary'),
          subsidizedMeals: sumBy(currentTransactionsInGroup, 'employerPart'),
          nettMeals: sumBy(currentTransactionsInGroup, 'employeePart'),
          total: totalSummary,
          isLastRow: true,
          centered: true,
        };
      }

      if (showGrossNet) {
        grossNettSplit = [
          {
            parentId,
            dateAndTime: t('payment'),
            restaurantName: t('gross'),
            total: transactionBrutoSum,
            isFirstRow: true,
            isLastRow: false,
          },
          {
            parentId,
            restaurantName: t('nett'),
            total: transactionNetSum,
            isFirstRow: false,
            isLastRow: true,
          },
        ];
      }

      if (order.guestType !== orderGuestsEnum.None) {
        guestRow = {
          parentId,
          dateAndTime: t('guests'),
          restaurantName: t(orderGuestsEnum[order.guestType]),
          total,
          isLastRow: true,
        };
      }

      return [
        ...allOrders,
        ...productsRows,
        commentsRows,
        debitTransferRows,
        ...employerEmployeeCut,
        ...grossNettSplit,
        ...paymentsRows,
        guestRow,
        dailySumRow,
      ].filter(item => item);
    }, []);
  }, [
    orderList,
    currentLanguageKey,
    dateBias,
    debitTransfersSentRequests,
    isAutoRefundRemarksPatchIncluded,
    isKlaReport,
    prePaidCard?.moneycardId,
    shouldShowCommentsRows,
    shouldShowPaymentRemarks,
    showEmployerEmployeePart,
    showGrossNet,
    showTransferDebit,
    checkIfMoneycardIsCredit,
    t,
  ]);

  const orderListWithPaymentsAndDishes = useMemo(
    () => getOrderListWithPaymentsAndDishes(),
    [getOrderListWithPaymentsAndDishes],
  );

  useEffect(() => {
    if (!orderListWithPaymentsAndDishes) {
      return;
    }

    const visibleItemsLocal = orderListWithPaymentsAndDishes.reduce((allRows, row) => {
      const isParentRow = !row.parentId;

      if (isParentRow) {
        if (!selectedFilter) {
          return allRows.concat(row);
        }

        const isCurrentRowRelevantForFilter = some(row.payments, {
          cardSuffix: selectedFilter.cardSuffix,
          paymentMethod: selectedFilter.paymentMethod,
        });
        if (isCurrentRowRelevantForFilter) {
          const summedFilteredTotal = sumBy(row.payments, ({cardSuffix, paymentMethod, total}) =>
            (cardSuffix === selectedFilter.cardSuffix && paymentMethod === selectedFilter.paymentMethod ? total : 0),
          );
          const newRowWithRelevantTotalByPaymentFilterSelect = {
            ...row,
            total: summedFilteredTotal,
          };

          return allRows.concat(newRowWithRelevantTotalByPaymentFilterSelect);
        }
      }

      const isParentItemCollapsed = openRows.includes(row.parentId);
      if (isParentItemCollapsed) {
        return allRows.concat(row);
      }

      return allRows;
    }, []);
    const currentFilterTotal = selectedFilter
      ? find(totals.payments, {cardSuffix: selectedFilter.cardSuffix, paymentMethod: selectedFilter.paymentMethod})
        .total
      : totals.total;

    const grossNetRows = [
      {
        restaurantName: t('gross'),
        total: totalGross || 0,
        isTotalAmountRow: true,
        marked: true,
      },
      {
        restaurantName: t('nett'),
        total: totalNett || 0,
        isTotalAmountRow: true,
        marked: true,
      },
    ];

    const visibleItemsWithSumRow = [
      ...visibleItemsLocal,
      ...[
        {
          dateAndTime: 'total_charges',
          total: currentFilterTotal,
          isTotalAmountRow: true,
          marked: true,
        },
        ...(showGrossNet ? grossNetRows : []),
      ],
    ];

    const extraHeightForOrderComments_ = visibleItemsWithSumRow.reduce((sum, {numberOfLines}) => {
      const regularRowHeight = 40;
      if (numberOfLines > 1) {
        return (sum + regularRowHeight) * (numberOfLines - 40);
      }
      return sum;
    }, 0);

    setVisibleItems(visibleItemsWithSumRow);
    setExtraHeightForOrderComments(extraHeightForOrderComments_);
  }, [
    orderListWithPaymentsAndDishes,
    openRows,
    selectedFilter,
    showGrossNet,
    t,
    totalGross,
    totalNett,
    totals?.payments,
    totals?.total,
  ]);

  const getCssClasses = ({index}) => {
    if (!visibleItems) {
      return;
    }

    const item = visibleItems[index];
    const classes = ['row'];

    const isParentRowCollapsed = item?.id && openRows.includes(item?.id);
    if (isParentRowCollapsed) {
      classes.push('collapsed');
    }

    const isParentRow = item?.id;
    if (isParentRow) {
      classes.push('parentRow');
    }

    const isFirstChildRow = item?.isFirstRow;
    if (isFirstChildRow) {
      classes.push('firstChildRow');
    }

    const isLastRowWithoutBorderBottom = item?.lastRowWithoutBorderBottom;
    if (isLastRowWithoutBorderBottom) {
      classes.push('noBottomBorder lastChildRow');
    }

    const isLastChildRow = item?.isLastRow;
    if (isLastChildRow) {
      classes.push('lastChildRow');
    }

    const isTotalAmountRow = item?.isTotalAmountRow;
    if (isTotalAmountRow) {
      classes.push('totalAmountRow');
    }

    const isMarked = item?.marked;
    if (isMarked) {
      classes.push('totalsRow');
    }

    return classes.join(' ');
  };

  const onFilterChange = ({paymentMethod, cardSuffix, paymentId}) => {
    clearOpenedRows();

    const isNoFilterOptionSelected = !paymentMethod && !cardSuffix;
    if (isNoFilterOptionSelected) {
      return setFilter('');
    }

    setFilter({paymentMethod, cardSuffix, isTenbisCredit: checkIfMoneycardIsCredit(paymentId)});
  };

  const allProps = {
    ...props,
    openRows,
    visibleItems,
    extraHeightForOrderComments,
    onFilterChange,
    getCssClasses,
    columns,
    toggleRow,
    dataTestId: 'order_id_',
  };

  return renderDesktopComponent ? <DesktopTransactionTable {...allProps} /> : <MobileTransactionTable {...allProps} />;
};

export default TransactionTable;
